import { UnfoldIcon } from "@primer/octicons-react";
import { marked, type Links, type Token } from "marked";
import { useMemo, useState } from "react";
import { useFile } from "../api/hooks";
import { type BlockOp, diffBlocks, foldUnchanged } from "../lib/blockdiff";
import { cn } from "../lib/utils";
import { Empty, ErrorMessage, Loading } from "./common";
import { tokensToHtml } from "./Markdown";
import { Button } from "./ui/button";

export const isMarkdown = (path: string) => /\.(md|markdown|mdx)$/i.test(path);

type Doc = { blocks: Token[]; links: Links };

function lex(source: string | null | undefined): Doc {
  const tokens = marked.lexer(source ?? "");
  return { blocks: tokens.filter((t) => t.type !== "space" && t.type !== "def"), links: tokens.links };
}

const BLOCK: Record<BlockOp<Token>["kind"], string> = {
  eq: "border-l-[3px] border-transparent",
  add: "border-l-[3px] border-success-emphasis bg-diff-add",
  del: "border-l-[3px] border-danger-emphasis bg-diff-del text-muted-foreground line-through decoration-danger",
};
const LABEL = { eq: "", add: "Added: ", del: "Removed: " };

function Block({ op, links }: { op: BlockOp<Token>; links: Links }) {
  const html = useMemo(() => tokensToHtml([op.item], links), [op.item, links]);
  return (
    <div className={cn("rounded-r-md px-4 py-2", BLOCK[op.kind])}>
      {LABEL[op.kind] && <span className="sr-only">{LABEL[op.kind]}</span>}
      <div className="markup" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

function Fold({ ops, links }: { ops: BlockOp<Token>[]; links: Links }) {
  const [open, setOpen] = useState(false);
  if (open) return ops.map((op, i) => <Block key={i} op={op} links={links} />);
  return (
    <Button variant="ghost" size="sm" className="w-full justify-start bg-diff-hunk text-muted-foreground" onClick={() => setOpen(true)}>
      <UnfoldIcon /> {ops.length} unchanged blocks
    </Button>
  );
}

/**
 * The rendered document with changed blocks marked: GitHub's rich diff for prose. Blocks are the
 * document's top-level elements (headings, paragraphs, lists, tables, code); an edit inside one
 * shows the whole block removed and re-added.
 */
export function MarkdownDiff({ repo, from, to, path, status }: { repo: string; from?: string; to: string; path: string; status: string }) {
  const before = useFile(repo, from ?? "", path, !!from && status !== "added");
  const after = useFile(repo, to, path, status !== "deleted");
  const pending = before.isLoading || after.isLoading;
  const error = before.error ?? after.error;
  const result = useMemo(() => {
    if (pending || error) return null;
    const a = lex(before.data?.content);
    const b = lex(after.data?.content);
    const ops = diffBlocks(a.blocks, b.blocks, (t) => t.raw.trim(), (t) => t.type);
    return { ops, links: { ...a.links, ...b.links } };
  }, [pending, error, before.data, after.data]);

  if (pending) return <Loading />;
  if (error) return <ErrorMessage error={error} />;
  if (!result) return null;
  if (!result.ops) return <Empty>This document is too large to compare rendered. Switch to Source to see the changes.</Empty>;
  if (!result.ops.some((op) => op.kind !== "eq")) return <Empty>Only whitespace or link definitions changed. Switch to Source to see them.</Empty>;
  return (
    <div className="flex flex-col gap-1 p-4">
      {foldUnchanged(result.ops).map((s, i) =>
        s.kind === "fold" ? <Fold key={i} ops={s.ops} links={result.links} /> : s.ops.map((op, k) => <Block key={`${i}-${k}`} op={op} links={result.links} />),
      )}
    </div>
  );
}
